import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Scale, Percent } from 'lucide-react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import styles from './TaxDrawer.module.css';

const TaxDrawer = ({ isOpen, onClose, onSave, tax }) => {
  const [formData, setFormData] = useState({
    tax_name: '',
    tax_percentage: ''
  });
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const fetchTaxDetail = async () => {
      if (tax && isOpen) {
        setIsFetching(true);
        try {
          // Pre-populate with existing data from props while fetching
          setFormData({
            tax_name: tax.tax_name || '',
            tax_percentage: tax.tax_percentage || ''
          });

          const response = await api.get(`/taxes/${tax.id}`);
          if (response.data.success) {
            const data = response.data.data;
            setFormData({
              tax_name: data.tax_name || '',
              tax_percentage: data.tax_percentage || ''
            });
          }
        } catch (error) {
          console.error('Error fetching tax details:', error);
          toast.error('Failed to fetch latest tax details');
        } finally {
          setIsFetching(false);
        }
      } else {
        setFormData({
          tax_name: '',
          tax_percentage: ''
        });
        setIsFetching(false);
      }
    };

    fetchTaxDetail();
  }, [tax, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tax_name.trim()) {
      toast.error('Tax name is required');
      return;
    }
    if (formData.tax_percentage === '') {
      toast.error('Tax percentage is required');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (tax) {
        response = await api.put(`/taxes/${tax.id}`, formData);
      } else {
        response = await api.post('/taxes', formData);
      }

      if (response.data.success) {
        toast.success(tax ? 'Tax updated successfully' : 'Tax created successfully');
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving tax:', error);
      toast.error(error.response?.data?.message || 'Failed to save tax');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={(e) => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{tax ? 'Edit Tax' : 'Add Tax'}</h2>
            <p>{tax ? 'Update details for this tax classification.' : 'Create a new tax rate for billing and invoices.'}</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {isFetching ? (
              <div className={styles['loading-container']}>
                <Loader2 size={40} className={styles['spinner']} />
                <span>Fetching tax details...</span>
              </div>
            ) : (
              <>
                {/* Tax Name */}
                <div className={styles['form-group']}>
                  <label htmlFor="tax_name">
                    <Scale size={16} />
                    <span>Tax Name <span className={styles['required-star']}>*</span></span>
                  </label>
                  <input
                    type="text"
                    id="tax_name"
                    name="tax_name"
                    placeholder="e.g. GST 18%, Service Tax"
                    value={formData.tax_name}
                    onChange={handleChange}
                    autoFocus
                    required
                  />
                </div>

                {/* Percentage */}
                <div className={styles['form-group']}>
                  <label htmlFor="tax_percentage">
                    <Percent size={16} />
                    <span>Percentage (%) <span className={styles['required-star']}>*</span></span>
                  </label>
                  <input
                    type="number"
                    id="tax_percentage"
                    name="tax_percentage"
                    placeholder="e.g. 18"
                    value={formData.tax_percentage}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    required
                  />
                </div>
              </>
            )}
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{tax ? 'Update Tax' : 'Save Tax'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaxDrawer;
